import * as React from 'react';
import { useT } from '../helpers/i18n';

import {
  faFacebookF, faInstagram, faYoutube, faXTwitter, faWhatsapp, faLinkedinIn,
  faGoogle, faTiktok, faPinterestP, faFoursquare
} from "@fortawesome/free-brands-svg-icons";
import {faLink} from "@fortawesome/pro-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useSelector} from "react-redux";
import {fetchBuilderSettingByKey} from "../helpers/MethodHelper";
import Checkers from "../helpers/class/Checkers";

interface SocialLinksProps {
  isManager?: boolean
  socialLinks?: any[]
  className?: string
}

const dynamicSocialIcon = (iconFa) => {
  switch (iconFa) {
  case 'fa-facebook-f':
  case 'fa-facebook':
    return faFacebookF;
  case 'fa-instagram':
    return faInstagram;
  case 'fa-youtube':
    return faYoutube;
  case 'fa-twitter':
  case 'fa-x-twitter':
    return faXTwitter;
  case 'fa-whatsapp':
    return faWhatsapp;
  case 'fa-linkedin':
  case 'fa-linkedin-in':
    return faLinkedinIn;
  case 'fa-google':
    return faGoogle;
  case 'fa-tiktok':
    return faTiktok;
  case 'fa-pinterest':
  case 'fa-pinterest-p':
    return faPinterestP;
  case 'fa-foursquare':
    return faFoursquare;
  default:
    return faLink;
  }
};

export default function SocialLinks ( props: SocialLinksProps ) {
  const t = useT();

  const showSocial = useSelector(
      state => Checkers.valueToBoolean(fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social'))
  );

  const facebookLink = useSelector(
      state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social_facebook')
  );

  const instagramLink = useSelector(
      state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social_instagram')
  );

  const linkedinLink = useSelector(
      state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social_linkedin')
  );

  const twitterLink = useSelector(
      state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social_twitter')
  );

  const youtubeLink = useSelector(
      state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social_youtube')
  );

  const whatappLink = useSelector(
      state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_show_social_whatsapp')
  );

  const hasBuilderSocialLinks = Checkers.isValidString(facebookLink) || Checkers.isValidString(instagramLink) ||
      Checkers.isValidString(linkedinLink) || Checkers.isValidString(twitterLink) ||
      Checkers.isValidString(youtubeLink) || Checkers.isValidString(whatappLink);

  const showingBuilderSocialLinks = showSocial && hasBuilderSocialLinks;

  const rawDynamicSocialLinks = !props.isManager && !showingBuilderSocialLinks && Checkers.isValidArray(props.socialLinks) ? props.socialLinks : [];

  const seenLinks = new Set();
  const dynamicSocialLinks = rawDynamicSocialLinks.filter((socialLink) => {
    if (seenLinks.has(socialLink.link)) return false;
    seenLinks.add(socialLink.link);
    return true;
  });

  const socialIconClass = "social-link-icon fs-20 me-2 mb-2 border d-flex justify-content-center align-items-center rounded-circle";

  if ( !showingBuilderSocialLinks && dynamicSocialLinks.length === 0 ) {
    return null;
  }

  return (
      <ul className={props.className != null ? props.className : "no-style mt-4 ps-0"}>
        {
          showingBuilderSocialLinks && Checkers.isValidString(facebookLink) &&
            <li className="inline no-padding">
              <a href={facebookLink} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: 'Facebook' })}>
                <FontAwesomeIcon icon={faFacebookF} className="fs-20" />
                <span className="hidden">Facebook</span>
              </a>
            </li>
        }
        {
          showingBuilderSocialLinks && Checkers.isValidString(instagramLink) &&
            <li className="inline no-padding">
              <a href={instagramLink} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: 'Instagram' })}>
                <FontAwesomeIcon icon={faInstagram} className="fs-20"/>
                <span className="hidden">Instagram</span>
              </a>
            </li>
        }
        {
          showingBuilderSocialLinks && Checkers.isValidString(youtubeLink) &&
            <li className="inline no-padding">
              <a href={youtubeLink} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: 'YouTube' })}>
                <FontAwesomeIcon icon={faYoutube} className="fs-20"/>
                <span className="hidden">YouTube</span>
              </a>
            </li>
        }
        {
          showingBuilderSocialLinks && Checkers.isValidString(twitterLink) &&
            <li className="inline no-padding">
              <a href={twitterLink} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: 'Twitter' })}>
                <FontAwesomeIcon icon={faXTwitter} className="fs-20"/>
                <span className="hidden">Twitter</span>
              </a>
            </li>
        }
        {
          showingBuilderSocialLinks && Checkers.isValidString(whatappLink) &&
            <li className="inline no-padding">
              <a href={whatappLink} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: 'WhatsApp' })}>
                <FontAwesomeIcon icon={faWhatsapp} className="fs-20"/>
                <span className="hidden">WhatsApp</span>
              </a>
            </li>
        }
        {
          showingBuilderSocialLinks && Checkers.isValidString(linkedinLink) &&
            <li className="inline no-padding">
              <a href={linkedinLink} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: 'LinkedIn' })}>
                <FontAwesomeIcon icon={faLinkedinIn} className="fs-20"/>
                <span className="hidden">LinkedIn</span>
              </a>
            </li>
        }
        {
          dynamicSocialLinks.map((socialLink) => (
              <li className="inline no-padding" key={socialLink.id}>
                <a href={socialLink.link} target="_blank" rel="noreferrer" className={socialIconClass} aria-label={t('a11y.socialPage', { name: socialLink.title })}>
                  <FontAwesomeIcon icon={dynamicSocialIcon(socialLink.iconFa)} className="fs-20"/>
                  <span className="hidden">{socialLink.title}</span>
                </a>
              </li>
          ))
        }
      </ul>
  );
}
